<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useQRCode } from '@vueuse/integrations/useQRCode'
import { useFeatureFlagStore } from '@/stores/features'
import { computed, ref } from 'vue'

const { t } = useI18n()

const docId = ref(Math.random().toString(36).substring(2, 8).toUpperCase())

// Generate a random signature for the document (ULID)
const signature = computed(() => {
  const timestamp = Date.now().toString(36)
  const randomPart = Math.random().toString(36).substring(2, 10).toUpperCase()
  return `${timestamp}-${randomPart}`
})

const qrCode = useQRCode(`https://app.finesthub.de/verify/${docId.value}/${signature.value}`)

const features = useFeatureFlagStore()
</script>

<template>
  <div>
    <div class="pagebreak"></div>

    <h2 class="mt-[3rem] text-xl font-semibold">{{ t('about.title') }}</h2>

    <p>
      {{ t('about.text') }}
    </p>

    <Fieldset v-if="features.enabled('pdfSignature')" :legend="t('signature.legend')" class="max-w-[180mm]">
      <div class="flex items-center gap-4">
        <div class="flex-shrink-0">
          <img :src="qrCode" :alt="t('signature.qr_alt')" class="size-32" />
        </div>
        <div>
          <i18n-t keypath="signature.description" tag="p">
            <template #link>
              <a :href="t('signature.verify_url')">{{ t('signature.verify_url') }}</a>
            </template>
          </i18n-t>
          <Divider type="dashed" />
          <div>
            {{ t('signature.document_id') }} <span class="font-medium">{{ docId }}</span>
          </div>
          <div>
            {{ t('signature.signature_label') }} <span class="font-medium">{{ signature }}</span>
          </div>
        </div>
      </div>
    </Fieldset>
  </div>
</template>

<style scoped>
.pagebreak {
  break-after: page;
}
</style>

<i18n>
{
  "de": {
    "about": {
      "title": "Über FinEstHub",
      "text": "FinEstHub verbindet Immobilieninvestoren und Banken und unterstützt sie bei der Suche nach der passenden Finanzierungen. Zudem bietet die Plattform Tools zur effizienten Verwaltung von Immobilien und Finanzierungsdaten."
    },
    "signature": {
      "legend": "Digitale Signatur",
      "qr_alt": "QR-Code",
      "description": "Dieses Dokument verfügt über eine digitale Signatur von FinEstHub. Die Signatur stellt die Authentizität und Integrität des Dokuments sicher. Um dieses Dokument zu verifizieren, scanne den QR-Code oder besuche {link}.",
      "verify_url": "https://finesthub.de/verify",
      "document_id": "Dokument-ID:",
      "signature_label": "Signatur:"
    }
  },
  "en": {
    "about": {
      "title": "About FinEstHub",
      "text": "FinEstHub connects real estate investors and banks, assisting them in finding suitable financing options. The platform also provides tools for efficient management of real estate and financing data."
    },
    "signature": {
      "legend": "Digital Signature",
      "qr_alt": "QR Code",
      "description": "This document has a digital signature provided by FinEstHub. The signature ensures the authenticity and integrity of the document. To verify this document scan the QR code or visit {link}.",
      "verify_url": "https://finesthub.de/verify",
      "document_id": "Document ID:",
      "signature_label": "Signature:"
    }
  }
}
</i18n>
